import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Printer, RefreshCw, Landmark, AlertCircle, ArrowUp, FileBarChart, CalendarCheck } from 'lucide-react';
import styles from './DailyReport.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { SMIIFY_API } from '../../config/api';
import DailyReportView, { REPORT_SECTIONS } from '../../components/DailyReportView/DailyReportView';
import ReportShareBox from '../../components/ReportShareBox/ReportShareBox';
import { shiftYmd, todayYmd, longDate } from '../../utils/money';

// =============================================================================
// Daily closing report
// The page fills the space below the top bar; the header (date, actions, section tabs)
// stays put and only the report scrolls. Section tabs jump to a part of the report and
// light up as you scroll.
// =============================================================================
export default function DailyReport() {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();
    const canShare = hasPermission('reports', 'share_daily_report');
    const canSeeDrawer = hasPermission('cashflow', 'view');
    const storeId = activeStore?._id || activeStore?.id;
    const isHQ = !storeId || storeId === 'global_hq' || activeStore?.isHQ;
    const [params, setParams] = useSearchParams();
    const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') || '') ? params.get('date') : todayYmd();
    const isToday = date === todayYmd();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [active, setActive] = useState(REPORT_SECTIONS[0].id);
    const [showTop, setShowTop] = useState(false);

    const pageRef = useRef(null);
    const scrollRef = useRef(null);
    const jumping = useRef(false);

    const setDate = (d) => setParams(d === todayYmd() ? {} : { date: d });

    /* ---------- the page takes the height left below the top bar ---------- */
    useLayoutEffect(() => {
        let frame = 0;
        const fit = () => {
            const el = pageRef.current;
            if (!el || window.matchMedia('print').matches) return;
            const top = el.getBoundingClientRect().top + (document.scrollingElement?.scrollTop || 0);
            el.style.height = `${Math.max(420, Math.floor(window.innerHeight - top))}px`;
            const extra = (document.scrollingElement?.scrollHeight || 0) - window.innerHeight;   // padding of the layout below us
            if (extra > 0) el.style.height = `${Math.max(420, el.offsetHeight - extra)}px`;
        };
        const onResize = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit); };
        fit();
        window.addEventListener('resize', onResize);
        return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', onResize); };
    }, []);

    /* ---------- report ---------- */
    const load = useCallback(async () => {
        if (isHQ) { setLoading(false); return; }
        setLoading(true); setError('');
        try {
            const res = await fetch(`${SMIIFY_API}/daily-report?date=${date}`, { headers: { Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId } });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not load the report.');
            setReport(json.data);
        } catch (e) {
            setReport(null);
            setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message);
        } finally { setLoading(false); }
    }, [date, storeId, isHQ]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); setActive(REPORT_SECTIONS[0].id); }, [date]);

    /* ---------- section tabs follow the scroll ---------- */
    const onScroll = useCallback(() => {
        const box = scrollRef.current;
        if (!box) return;
        setShowTop(box.scrollTop > 500);
        if (jumping.current) return;
        const boxTop = box.getBoundingClientRect().top;
        let current = REPORT_SECTIONS[0].id;
        REPORT_SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el && el.getBoundingClientRect().top - boxTop <= 80) current = id;
        });
        if (box.scrollTop + box.clientHeight >= box.scrollHeight - 4) current = REPORT_SECTIONS[REPORT_SECTIONS.length - 1].id;   // reached the end
        setActive(current);
    }, []);

    const jump = (id) => {
        const box = scrollRef.current;
        const el = document.getElementById(id);
        if (!box || !el) return;
        setActive(id);
        jumping.current = true;
        box.scrollTo({ top: box.scrollTop + el.getBoundingClientRect().top - box.getBoundingClientRect().top - 12, behavior: 'smooth' });
        setTimeout(() => { jumping.current = false; }, 600);
    };

    const status = report?.drawer?.closing ? { text: 'Drawer closed', cls: styles.pillClosed } : isToday ? { text: 'Live', cls: styles.pillLive } : { text: 'Not closed', cls: styles.pillOpen };

    return (
        <div className={styles.page} ref={pageRef}>
            {/* ---------------- header: never scrolls ---------------- */}
            <header className={styles.header}>
                <div className={styles.topRow}>
                    <div className={styles.titleBlock}>
                        <span className={styles.titleIcon}><FileBarChart size={18} /></span>
                        <div>
                            <h1>Daily closing report {report && <span className={`${styles.pill} ${status.cls}`}>{status.text}</span>}</h1>
                            <p>{longDate(date)} · {activeStore?.storeName || activeStore?.name || 'this branch'}</p>
                        </div>
                    </div>
                    <div className={styles.tools}>
                        <div className={styles.dateNav}>
                            <button type="button" onClick={() => setDate(shiftYmd(date, -1))} aria-label="Previous day"><ChevronLeft size={15} /></button>
                            <input type="date" value={date} max={todayYmd()} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Report date" />
                            <button type="button" onClick={() => setDate(shiftYmd(date, 1))} disabled={isToday} aria-label="Next day"><ChevronRight size={15} /></button>
                        </div>
                        {!isToday && <button type="button" className={styles.ghost} onClick={() => setDate(todayYmd())}><CalendarCheck size={14} /> Today</button>}
                        <button type="button" className={styles.iconBtn} onClick={load} disabled={loading} aria-label="Refresh" title="Refresh"><RefreshCw size={14} className={loading ? styles.spin : ''} /></button>
                        {canSeeDrawer && <button type="button" className={styles.ghost} onClick={() => navigate(`/cash-flow${!isToday ? `?date=${date}` : ''}`)}><Landmark size={14} /> Cash drawer</button>}
                        <button type="button" className={styles.primary} onClick={() => window.print()} disabled={!report}><Printer size={14} /> Print / PDF</button>
                    </div>
                </div>
                {report && !isHQ && (
                    <nav className={styles.sections} aria-label="Report sections">
                        {REPORT_SECTIONS.map((s) => (
                            <button key={s.id} type="button" className={active === s.id ? styles.sectionOn : ''} onClick={() => jump(s.id)}>{s.label}</button>
                        ))}
                    </nav>
                )}
            </header>

            {/* ---------------- only this part scrolls ---------------- */}
            <main className={styles.scroll} ref={scrollRef} onScroll={onScroll}>
                {isHQ ? (
                    <div className={styles.state}><AlertCircle size={22} /><strong>Select a branch</strong><span>The daily report is made for one branch at a time.</span></div>
                ) : loading && !report ? (
                    <div className={styles.skeleton} aria-label="Loading report">
                        <i className={styles.skHead} />
                        <div className={styles.skRow}>{[0, 1, 2, 3, 4].map((k) => <i key={k} />)}</div>
                        <i className={styles.skBig} />
                        <div className={styles.skTwo}><i /><i /></div>
                    </div>
                ) : error ? (
                    <div className={styles.state}><AlertCircle size={22} /><strong>Report could not be loaded</strong><span>{error}</span><button type="button" className={styles.ghost} onClick={load}>Try again</button></div>
                ) : report && (
                    <div className={`${styles.inner} ${loading ? styles.dim : ''}`}>
                        {canShare && <div className={styles.share}><ReportShareBox key={date} storeId={storeId} date={date} storeName={report?.store?.name} /></div>}
                        <DailyReportView report={report} />
                    </div>
                )}
                {showTop && (
                    <button type="button" className={styles.toTop} onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Back to top"><ArrowUp size={16} /></button>
                )}
            </main>
        </div>
    );
}
